import { useLazyQuery } from '@apollo/client';
import { ErrorCode, Gender, ICareLevel, Rights } from '@dokucoach/common-types';
import { Box } from '@material-ui/core';
import ArrowButton from 'components/ArrowButton';
import { useContextAuthManager } from 'components/AuthManager';
import { useContextSnackbar } from 'components/Snackbar/context';
import TooltipAction from 'components/Tooltip';
import routePaths from 'constants/route-paths';
import { find } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import {
  GET_RESIDENT_CARE_LEVEL,
  GET_RESIDENT_HEADER_FLAGS,
  NursingHomeHeadPropType,
  ResidentHeadPropType,
} from 'services/residenHead';
import { AvatarImageType, getAvatarImage } from 'utils/getAvatarImage';
import { getPath } from 'utils/path';
import { useContextLayout } from '../Layout/context';
import ResidentListing from './components/ResidentListing';
import {
  BoxAvatar,
  BoxAvatarImg,
  BoxContainerStyle,
  BoxIcon,
  BoxInfo,
  TypographyP,
  TypographyPClickable,
} from './styled-components';

export const getIcons = (data: Record<string, boolean>): string[] => {
  const icons = [];
  if (data.isAbsent) {
    icons.push('absent');
  }
  if (data.hasInfectiousDisease) {
    icons.push('infectious');
  }
  if (data.hasMultiResistantGerm) {
    icons.push('multiresistant');
  }
  if (data.hasCognitiveLimitations) {
    icons.push('cognitive');
  }
  if (data.isDyingPhase) {
    icons.push('coffin');
  }
  if (data.hasMedicationAdministration) {
    icons.push('medication');
  }
  return icons;
};

const ResidentHeader: FC = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { showApiError } = useContextSnackbar();
  const { hasPermission } = useContextAuthManager();
  const location = useLocation();
  const [icons, setIcons] = useState<string[]>([]);
  const { shouldRefetchHeaderFlags, setShouldRefetchHeaderFlags } = useContextLayout();

  const path = matchPath(location.pathname, {
    path: routePaths.GROUP + routePaths.RESIDENT,
  }) as { params: { residentId: string; groupId: string } };

  const [nursingHead, setNursingHead] = useState<NursingHomeHeadPropType | undefined>();
  const [residentHead, setResidentHead] = useState<ResidentHeadPropType | undefined>();
  const [residentCareLevelData, setResidentCareLevelData] = useState<string | undefined>();

  const { residentGroups } = useContextLayout();

  const [
    getResidentCareLevelData,
    { data: careLevelsData, error: careLevelsError, refetch: refetchCareLevel },
  ] = useLazyQuery(GET_RESIDENT_CARE_LEVEL);

  const [
    getResidentHeaderFlagData,
    {
      data: residentHeaderFlagData,
      error: residentHeaderFlagError,
      refetch: refetchResidentHeaderFlags,
    },
  ] = useLazyQuery(GET_RESIDENT_HEADER_FLAGS);

  useEffect(() => {
    if (shouldRefetchHeaderFlags && refetchResidentHeaderFlags) {
      refetchResidentHeaderFlags().then(() => {
        setShouldRefetchHeaderFlags(undefined);
      });
    }
  }, [shouldRefetchHeaderFlags, setShouldRefetchHeaderFlags]);

  useEffect(() => {
    if (residentHeaderFlagError) {
      showApiError(residentHeaderFlagError);
      return;
    }
    if (residentHeaderFlagData) {
      const { getResidentHeaderFlags, getResident } = residentHeaderFlagData;
      const headerFlags = Object.assign(getResidentHeaderFlags, getResident);
      setIcons(getIcons(headerFlags));
    }
  }, [residentHeaderFlagData, residentHeaderFlagError]);

  useEffect(() => {
    if (careLevelsError && careLevelsError.message !== ErrorCode.RESIDENT_CONTRACT_NOT_FOUND) {
      showApiError(careLevelsError);
      return;
    }

    if (careLevelsError && careLevelsError.message === ErrorCode.RESIDENT_CONTRACT_NOT_FOUND) {
      setResidentCareLevelData(undefined);
      return;
    }

    if (careLevelsData) {
      const levels = careLevelsData.getResidentContractByResidentId.careLevels;
      if (!levels || !levels.length) {
        setResidentCareLevelData(undefined);
        return;
      }
      const provisionalLevel = levels.find((level: Partial<ICareLevel>) => level.provisional);
      if (provisionalLevel) {
        setResidentCareLevelData(provisionalLevel.level);
      } else {
        const sortedCareLevelsByDate: ICareLevel[] =
          careLevelsData.getResidentContractByResidentId.careLevels.sort(
            (a: Partial<ICareLevel>, b: Partial<ICareLevel>) =>
              b.date && a.date ? new Date(b.date).getTime() - new Date(a.date).getTime() : 0,
          );
        setResidentCareLevelData(sortedCareLevelsByDate[0].level);
      }
    }
  }, [careLevelsData, careLevelsError]);

  useEffect(() => {
    const params = {
      residentId: path?.params?.residentId,
      groupId: path?.params?.groupId,
    } as { groupId: string; residentId: string };

    if (params.groupId && params.residentId) {
      const tempNursingHead = find(residentGroups, { id: params.groupId });
      setNursingHead(tempNursingHead);
      if (tempNursingHead) {
        setResidentHead(find(tempNursingHead.residents, { id: params.residentId }));
      }
    } else {
      setResidentHead(undefined);
    }
  }, [residentGroups, path]);

  useEffect(() => {
    if (residentHead) {
      getResidentCareLevelData({ variables: { residentId: residentHead.id } });
      getResidentHeaderFlagData({ variables: { residentId: residentHead.id } });
    }
  }, [residentHead]);

  const getDestination = (url: string): string => {
    const lastPart = url.replace(/.+\/resident\/[^/]+\//, '');
    if (lastPart.includes(routePaths.DASHBOARD)) return '';
    return `/${lastPart}`;
  };

  const onPrevious = () => {
    if (!nursingHead) return;
    const index = nursingHead.residents.findIndex((item) => item.id === path.params.residentId);
    const residentId =
      index <= 0
        ? nursingHead?.residents[nursingHead?.residents.length - 1].id
        : nursingHead?.residents[index - 1].id;
    history.push(
      getPath([routePaths.GROUP, routePaths.RESIDENT, getDestination(window.location.href)], {
        residentId,
        groupId: path.params.groupId,
      }),
    );
  };

  const onNext = () => {
    if (!nursingHead) return;
    const index = nursingHead.residents.findIndex((item) => item.id === path.params.residentId);
    const residentId =
      index < 0 || index === nursingHead.residents.length - 1
        ? nursingHead.residents[0].id
        : nursingHead.residents[index + 1].id;
    history.push(
      getPath([routePaths.GROUP, routePaths.RESIDENT, getDestination(window.location.href)], {
        residentId,
        groupId: path.params.groupId,
      }),
    );
  };

  // TODO: Navigate to base data of selected resident
  const matchedGroupId = matchPath(location.pathname, {
    path: routePaths.GROUP + routePaths.RESIDENT,
  }) as { params: { groupId: string } };

  const onClickNavigateBaseData = () => {
    history.push(
      getPath(
        [
          routePaths.DATA_RESIDENT,
          routePaths.DATA_GENERAL,
          `?from_group=${matchedGroupId.params.groupId}`,
        ],
        {
          entityId: residentHead?.id,
        },
      ),
    );
  };

  return nursingHead && residentHead ? (
    <BoxContainerStyle
      sx={{
        display: 'flex',
      }}
    >
      <BoxInfo
        sx={{
          flexGrow: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ArrowButton direction="up" onClick={onPrevious} margin="0 0.5rem 0 0" />
          <ArrowButton direction="down" onClick={onNext} margin="0 0.5rem 0 0" />
          <ResidentListing nursingHead={nursingHead} residentHead={residentHead} />
          {hasPermission(Rights.RESIDENTS.READ) && (
            <TypographyPClickable onClick={onClickNavigateBaseData}>
              {t('resident_header_change_to_base_data')}
            </TypographyPClickable>
          )}
        </Box>
        <BoxIcon
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <TypographyP>
            {(residentHead.id &&
              residentCareLevelData &&
              `${t('nursingLevel')} ${residentCareLevelData}`) ||
              t('resident_header_no_care_level_set')}
          </TypographyP>
          {icons.length
            ? icons
                .filter((icon: string) => icon !== 'absent')
                .map((icon) => <TooltipAction variant={icon} key={icon} />)
            : null}
        </BoxIcon>
        <BoxIcon
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {residentHead && <TypographyP>{residentHead.bed?.label ?? t('no_bed_set')}</TypographyP>}
          {icons.includes('absent') && <TooltipAction variant={'absent'} />}
        </BoxIcon>
      </BoxInfo>
      <BoxAvatar className="d-none d-md-block">
        {'profilePictureUrl' in residentHead && residentHead.profilePictureUrl ? (
          <BoxAvatarImg src={residentHead.profilePictureUrl ?? undefined} />
        ) : (
          <SVG src={getAvatarImage(residentHead?.name.gender as Gender, AvatarImageType.OLD)} />
        )}
      </BoxAvatar>
    </BoxContainerStyle>
  ) : (
    <div />
  );
};

export default ResidentHeader;
